import { useState, useRef, useEffect } from "react";
import uniqBy from "lodash/uniqBy";
import SimpleReactValidator from "simple-react-validator";
import Loader from "../../../components/loader";
import Layout from "../../../layout/Layout";
import AppService from "../../../services/app.service";
import Select from "react-select";
import AlertDialogSlide from "../../../components/deleteModal";
import auth from "../../../services/auth";
import {
  getMultiSelectOptionsInCaseOfInactiveAndDelete,
  handleMultiSelectChange,
  selectAllOptionCohorts,
} from "../../../utils/utils";

const initialState = {
  firstName: "",
  lastName: "",
  email: "",
  accountAccId: null,
  cohort: [],
  active: true,
  progressStatus: 1,
};

let prevCohort = "";
let prevSelectCohort = [];

const CreateOrEditCohortParticipant = (props) => {
  const [formState, setFormState] = useState({ ...initialState });
  const [cohortList, setCohortList] = useState([]);
  const [participantData, setParticipantData] = useState();
  const [isActiveButtonDisable, setIsActiveButtonDisable] = useState(false);
  const [, forceUpdate] = useState(0);
  const simpleValidator = useRef(
    new SimpleReactValidator({ autoForceUpdate: { forceUpdate: forceUpdate } })
  );
  const [loading, setLoading] = useState(false);
  const authenticatedUserDetail = auth.getUserDetails();
  const [isEdit, setIsEdit] = useState(
    !props["match"].params.participantId
      ? { title: "Add", submitButtonTitle: "Create" }
      : {
          participantId: props["match"].params.participantId,
          title: "Edit",
          submitButtonTitle: "Update",
        }
  );
  const [isShowingModal, setIsShowingModal] = useState(false);
  const [uniqueEmail, setUniqueEmail] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [showCohortChangeModal, setShowCohortChangeModal] = useState(false);

  const loadData = async () => {
    let user = auth.getUserDetails();
    const accId = user.accId;
    setLoading(true);

    const cohortsList = await AppService.getAllWithData(
      "cohorts/getAllActiveCohorts",
      authenticatedUserDetail?.accId
    );

    let allCohortOptions = [];

    if (isEdit.participantId) {
      const cohortParticipant = await AppService.get(
        isEdit.participantId,
        "cohortParticipant"
      );

      const { cohorts, ...participantData } = cohortParticipant?.data;
      const selectedCohort = cohorts.map((cohort) => {
        return { value: cohort.value, label: cohort.label };
      });
      allCohortOptions = [...selectedCohort];
      setFormState((prevState) => ({
        ...prevState,
        ...participantData,
        cohort: selectedCohort,
        active: cohortParticipant?.data?.active ?? prevState.active,
        senderName:
          authenticatedUserDetail.firstName +
          " " +
          authenticatedUserDetail.lastName,
      }));

      prevCohort = selectedCohort.map((cohort) => cohort?.label).join(", ");
      prevSelectCohort = selectedCohort;
      setParticipantData(cohortParticipant?.data);
      setIsActiveButtonDisable(false);
    }

    const mappedCohorts = cohortsList?.data?.data.map((cohort) => ({
      value: cohort.id,
      label: cohort.name,
    }));

    allCohortOptions = [...allCohortOptions, ...mappedCohorts];

    allCohortOptions = uniqBy(allCohortOptions, "value");

    setCohortList([...allCohortOptions]);
    setFormState((prevState) => {
      return { ...prevState, accountAccId: accId };
    });
    setLoading(false);
  };

  const handleMultiSelectCohortChange = (value, action) => {
    const list = handleMultiSelectChange(
      value,
      action,
      cohortList,
      formState.cohort,
      selectAllOptionCohorts
    );
    setFormState((prevState) => ({ ...prevState, cohort: list }));
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleChange = (event) => {
    const newState = { [event.target.name]: event.target.value };
    setFormState((prevState) => ({ ...prevState, ...newState }));
    if (event.target.name == "email") {
      setUniqueEmail(false);
    }
  };

  const handleCheckBox = (event) => {
    const newState = { [event.target.name]: event.target.checked };
    setFormState((prevState) => ({ ...prevState, ...newState }));
    setIsShowingModal(!event.target.checked ? true : false);
  };

  const checkCohortChange = (e) => {
    e.preventDefault();
    if (!simpleValidator.current.allValid()) {
      return simpleValidator.current.showMessages();
    }
    const selectedCohort = formState.cohort
      .map((cohort) => cohort?.label)
      .join(", ");
    if (formState?.id && prevCohort !== selectedCohort) {
      setShowCohortChangeModal(true);
    } else {
      submitForm();
    }
  };

  const handleNoForCohortChange = () => {
    setFormState((prevState) => ({ ...prevState, cohort: prevSelectCohort }));
    setShowCohortChangeModal(false);
  };

  const submitForm = async (e) => {
    setShowCohortChangeModal(false);
    setUniqueEmail(false);
    setSaveError("");
    setLoading(true);

    let saved = false;
    try {
      const cohortParticipant = await AppService.submit(
        {
          ...formState,
          cohorts: formState.cohort,
          senderName:
            authenticatedUserDetail.firstName +
            " " +
            authenticatedUserDetail.lastName,
        },
        "cohortParticipant"
      );
      saved = cohortParticipant.status === 201;
    } catch (error) {
      if (error.response?.status === 409) {
        setUniqueEmail(true);
      } else {
        setSaveError("Could not save the participant. Please try again.");
      }
    } finally {
      setLoading(false);
    }

    if (saved) {
      props.history.push("/cohorts-participants");
    }
  };

  const handleCancel = (e) => {
    props.history.push("/cohorts-participants");
  };

  const handleAlertDialogNo = () => {
    setFormState((prevState) => ({
      ...prevState,
      active: true,
    }));
    setIsShowingModal(false);
  };

  const handleAlertDialogYes = () => {
    setIsShowingModal(false);
  };

  return (
    <div>
      <Layout
        title="Participant Management"
        history={props.history}
        titleUrl="/cohorts-participants"
        subTitle={`${isEdit.title} Participant`}
        activeParticipantLink={true}
        showBreadCrumb={true}
      >
        <section className="forms">
          <div className="container-fluid">
            <div className="row">
              {/* <!-- Horizontal Form--> */}
              <div className="col-lg-12">
                <div className="card">
                  <div className="card-header d-flex align-items-center">
                    <h3 className="sub-heading">{isEdit.title} Participant</h3>
                  </div>
                  <div className="card-body">
                    <form className="form-horizontal" noValidate={true}>
                      <div className="form-group row">
                        <label className="col-sm-3 form-control-label required label">
                          First Name
                        </label>
                        <div className="col-sm-9">
                          <input
                            name="firstName"
                            type="text"
                            maxLength={255}
                            placeholder="First Name"
                            className="form-control form-control-success fields-font"
                            value={formState.firstName}
                            onChange={(event) => handleChange(event)}
                          />
                          {simpleValidator.current.message(
                            "name",
                            formState.firstName,
                            "required"
                          )}
                        </div>
                      </div>
                      <div className="form-group row">
                        <label className="col-sm-3 form-control-label required label">
                          Last Name
                        </label>
                        <div className="col-sm-9">
                          <input
                            name="lastName"
                            type="text"
                            maxLength={255}
                            placeholder="Last Name"
                            className="form-control form-control-success fields-font"
                            value={formState.lastName}
                            onChange={(event) => handleChange(event)}
                          />
                          {simpleValidator.current.message(
                            "name",
                            formState.lastName,
                            "required"
                          )}
                        </div>
                      </div>

                      <div className="form-group row">
                        <label className="col-sm-3 form-control-label required label">
                          Email
                        </label>
                        <div className="col-sm-9">
                          <input
                            name="email"
                            type="text"
                            maxLength={50}
                            placeholder="Email"
                            className="form-control form-control-success fields-font"
                            value={formState.email}
                            onChange={(event) => handleChange(event)}
                            readOnly={formState?.id}
                          />
                          {simpleValidator.current.message(
                            "email",
                            formState.email,
                            "required|email"
                          )}
                          {uniqueEmail && (
                            <div className="error">
                              The email must be unique
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="form-group row">
                        <label className="col-sm-3 form-control-label required label">
                          Select Cohort
                        </label>
                        <div className="col-sm-9">
                          <Select
                            name="cohort"
                            isMulti={true}
                            options={getMultiSelectOptionsInCaseOfInactiveAndDelete(
                              cohortList,
                              formState.cohort,
                              selectAllOptionCohorts
                            )}
                            value={formState.cohort}
                            className="basic-multi-select fields-font"
                            onChange={handleMultiSelectCohortChange}
                          />
                          {simpleValidator.current.message(
                            "cohort",
                            formState.cohort,
                            "required"
                          )}
                        </div>
                      </div>
                      <div className="form-group row mt-3">
                        <label className="col-sm-12 form-control-label label formLabel">
                          <b className="textRed fontSize">Note:</b> We will set
                          the participant's progress status default to "In
                          cohort." You can update the participant's progress
                          status at any time in Cohorts Insight Dashboard.
                        </label>
                      </div>
                      {isEdit.participantId && (
                        <div className="form-group row">
                          <label className="col-sm-3 form-control-label"></label>
                          <div className="col-sm-9">
                            <div className="custom-control custom-checkbox checkbox-xl">
                              <input
                                id="active"
                                type="checkbox"
                                checked={formState.active}
                                name="active"
                                className="custom-control-input"
                                onChange={(event) => handleCheckBox(event)}
                                disabled={isActiveButtonDisable}
                              />
                              <label
                                className="custom-control-label"
                                htmlFor="active"
                              >
                                Active
                              </label>
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="form-group row">
                        <div className="col-sm-9 offset-sm-3">
                          {saveError && (
                            <div className="error">{saveError}</div>
                          )}
                          <button
                            type="submit"
                            onClick={checkCohortChange}
                            className="btn btn-submit float-right buttons"
                          >
                            {isEdit.submitButtonTitle}
                          </button>
                          <button
                            type="button"
                            className="btn btn-cancel float-right buttons"
                            onClick={handleCancel}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </Layout>
      <AlertDialogSlide
        show={isShowingModal}
        handleNo={handleAlertDialogNo}
        handleYes={handleAlertDialogYes}
        title="Warning!"
        positiveButtonTitle="Yes"
        negativeButtonTitle="No"
      >
        <p>
          Deactivating this participant will also deactivate all surveys,
          assignments, and simulations associated with{" "}
          <span className="span">
            {participantData?.firstName + " " + participantData?.lastName}
          </span>
          . Do you want to continue?
        </p>
      </AlertDialogSlide>
      <AlertDialogSlide
        show={showCohortChangeModal}
        handleNo={handleNoForCohortChange}
        handleYes={submitForm}
        title="Warning!"
        positiveButtonTitle="Okay"
        negativeButtonTitle="Cancel"
      >
        <p>
          <span>
            All surveys and simulation links associated with the removed cohorts
            will be permanently deleted.
          </span>
          <span>
            Assignments will be automatically assigned to participants in the
            newly added cohorts.
          </span>
          Do you want to continue the changes with{" "}
          <span className="span">
            {participantData?.firstName + " " + participantData?.lastName}
          </span>
          ?
        </p>
      </AlertDialogSlide>
      {loading && (
        <div className="overlay">
          <Loader loading={loading} />
        </div>
      )}
    </div>
  );
};

export default CreateOrEditCohortParticipant;
